import { API, APIError, Hook } from "./api"
import {
    AccountSMSOTPBody,
    AddressBody,
    AddressResponse,
    AuthCustomerResponse,
    BankAccountBody,
    BankAccountResponse,
    CartBody,
    CartConfirmBody,
    CartConfirmResponse,
    CartItemBody,
    CartParams,
    CartPaymentResponse,
    CartResponse,
    CartShippingResponse,
    CartVoucherBody,
    CheckAccountBody,
    CheckAccountResponse,
    CommissionBookingsAggregateParams,
    CommissionBookingsAggregateResponse,
    CommissionBookingsParams,
    CommissionBookingsResponse,
    CommissionRuleResponse,
    CommissionRulesParams,
    CommissionRulesResponse,
    CommissionStatementResponse,
    CommissionStatementsParams,
    CommissionStatementsResponse,
    ConfirmPaymentTokenBody,
    ConfirmPaymentTokenResponse,
    ContactBody,
    ContactCategoryResponse,
    CreateAccountBody,
    CustomerAggregationBody,
    CustomerAttributesBody,
    CustomerAttributesResponse,
    CustomerBody,
    CustomerExpressionResponse,
    CustomerParams,
    CustomerReferrerBody,
    CustomerResponse,
    CustomerSegmentationBody,
    CustomerTrackingBody,
    EventAttendancesParams,
    EventAttendancesResponse,
    EventCategoryParams,
    EventCategoryResponse,
    EventCreateAttendancesBody,
    EventCreateAttendancesResponse,
    EventResponse,
    EventsParams,
    EventsResponse,
    Language,
    LoginBody,
    LoginResponse,
    OrderBody,
    OrderParams,
    OrderResponse,
    OrdersResponse,
    PaymentTokenBody,
    PaymentTokenResponse,
    ProductsParams,
    ProductsResponse,
    PromotionalGroupParams,
    PromotionalGroupResponse,
    PromotionalParams,
    PromotionalResponse,
    ResearchBody,
    ResearchParams,
    ResearchResponse,
    ResearchesResponse,
    ResetAccountBody,
    ResetAccountConfirmBody,
    SubscriptionBody,
    SubscriptionPaymentMethodsResponse,
    SubscriptionResponse,
    SubscriptionShippingCarriersResponse,
    SubscriptionTokenLinkBody,
    SubscriptionTokenLinkResponse,
    UpdateEmailBody,
    UpdatePasswordBody,
} from "./types"

import { State } from "./state"

export type SDKOptions = {
    url: string
    storage?: Storage
    storageKey: string
    crossDomain: string
    uid: string
    password: string
}

export class SDK {
    public state: State
    public api: API

    constructor(options: SDKOptions) {
        this.state = new State(options, options?.storage)
        this.api = new API(options, this.state)
    }

    mount(hooks: Hook[]) {
        hooks.forEach(hook => this.api.addHook(hook))
    }

    unmount() {
        this.api.removeHooks()
    }

    // #region Auth

    async login(body: LoginBody, language?: Language) {
        return await this.api.auth.login(body, language)
    }

    logout() {
        return this.api.auth.logout()
    }

    async reAuth() {
        return await this.api.callHooks("reAuth")
    }

    async getCustomerId() {
        const response = await this.api.get<AuthCustomerResponse>("/auth/customer", "customerWeakToken")
        this.state.customer_id = response.customer_id
        return response
    }

    async register(body: CustomerBody & { password: string }, language?: Language) {
        const { password, ...clone } = body
        const customer = await this.createCustomer(clone, language)

        const response = await this.createAccount(
            {
                customer_id: customer.id,
                access_token: customer.address.access_token,
                password: password,
            },
            language
        )
        this.state.customerWeakToken = response.token.weak.value
        this.state.customerStrongToken = response.token.strong.value
        this.state.customer_id = response.customer_id

        return response
    }

    validateToken(token: string, interval?: number) {
        return this.api.auth.validateToken(token, interval)
    }

    // #endregion Auth
    // #region Account

    async createAccount(body: CreateAccountBody, language?: Language) {
        const response = await this.api.post<LoginResponse>("/account", "consumerToken", language, body)
        this.state.customerWeakToken = response.token.weak.value
        this.state.customerStrongToken = response.token.strong.value
        this.state.customer_id = response.customer_id
        return response
    }

    async checkAccount(body: CheckAccountBody, language?: Language) {
        return await this.api.post<CheckAccountResponse>("/account/check", "consumerToken", language, body)
    }

    async updateEmail(body: UpdateEmailBody, language?: Language) {
        return await this.api.patch("/account/email", "customerStrongToken", language, body)
    }

    async postEmailTokenLinkVerification(token: string) {
        try {
            await this.api.post(`/customer/token_link/email_verification/${token}`, "consumerToken")
            return true
        } catch (err) {
            const error = err as APIError
            if (error.status === 404) return false
            throw error
        }
    }

    async updatePassword(body: UpdatePasswordBody, language?: Language) {
        return await this.api.patch("/account/password", "customerStrongToken", language, body)
    }

    async requestPasswordReset(body: ResetAccountBody, language?: Language) {
        return await this.api.post("/account/reset", "consumerToken", language, body)
    }

    async confirmPasswordReset(body: ResetAccountConfirmBody, language?: Language) {
        const response = await this.api.post<LoginResponse>("/account/reset/confirm", "consumerToken", language, body)
        this.state.customerWeakToken = response.token.weak.value
        this.state.customerStrongToken = response.token.strong.value
        this.state.customer_id = response.customer_id
        return response
    }

    async requestSmsOneTimePassword(body: AccountSMSOTPBody, language?: Language) {
        return await this.api.post("/account/smsotp", "consumerToken", language, body)
    }

    // #endregion Account
    // #region Customer

    async createCustomer(body: CustomerBody, language?: Language) {
        return await this.api.post<CustomerResponse>("/customer", "consumerToken", language, body)
    }

    async getCustomer(customer_id: string, params?: CustomerParams, language?: Language) {
        return await this.api.get<CustomerResponse>(
            `/customer/${customer_id}`,
            this.state.customerWeakToken ? "customerWeakToken" : "consumerToken",
            language,
            params
        )
    }

    async updateCustomer(
        customer_id: string,
        body: CustomerBody,
        token?: "customerWeakToken",
        params?: CustomerParams,
        language?: Language
    ) {
        if (params?.access_token && this.state.customerStrongToken) {
            params.access_token = undefined
        }
        return await this.api.patch<CustomerResponse>(
            `/customer/${customer_id}`,
            token || (this.state.customerStrongToken ? "customerStrongToken" : "consumerToken"),
            language,
            body,
            params
        )
    }

    async getCustomerReferrer(customer_id: string, params?: CustomerParams, language?: Language) {
        try {
            await this.api.get(
                `/customer/${customer_id}/referrer`,
                this.state.customerWeakToken ? "customerWeakToken" : "consumerToken",
                language,
                params
            )
            return { referrer: true }
        } catch (err) {
            const error = err as APIError
            if (error.status === 404) {
                return { referrer: false }
            }
            throw error
        }
    }

    async updateCustomerReferrer(
        customer_id: string,
        body: CustomerReferrerBody,
        params?: CustomerParams,
        language?: Language,
        token?: "consumerToken"
    ) {
        return await this.api.put(
            `/customer/${customer_id}/referrer`,
            token || (this.state.customerWeakToken ? "customerWeakToken" : "consumerToken"),
            language,
            body,
            params
        )
    }

    async createAddress(customer_id: string, body: AddressBody, params?: CustomerParams, language?: Language) {
        return await this.api.post<AddressResponse>(
            `/customer/${customer_id}/address`,
            this.state.customerStrongToken ? "customerStrongToken" : "consumerToken",
            language,
            body,
            params
        )
    }

    async getAddress(customer_id: string, address_id: string, params?: CustomerParams, language?: Language) {
        return await this.api.get<AddressResponse>(
            `/customer/${customer_id}/address/${address_id}`,
            this.state.customerWeakToken ? "customerWeakToken" : "consumerToken",
            language,
            params
        )
    }

    async updateAddress(
        customer_id: string,
        address_id: string,
        body: AddressBody,
        params?: CustomerParams,
        language?: Language
    ) {
        return await this.api.patch<AddressResponse>(
            `/customer/${customer_id}/address/${address_id}`,
            this.state.customerStrongToken ? "customerStrongToken" : "consumerToken",
            language,
            body,
            params
        )
    }

    async removeAddress(customer_id: string, address_id: string, params?: CustomerParams, language?: Language) {
        return await this.api.delete(
            `/customer/${customer_id}/address/${address_id}`,
            this.state.customerStrongToken ? "customerStrongToken" : "consumerToken",
            language,
            params
        )
    }

    async createBankAccount(customer_id: string, body: BankAccountBody, params?: CustomerParams, language?: Language) {
        return await this.api.post<BankAccountResponse>(
            `/customer/${customer_id}/payment/bank_account`,
            this.state.customerStrongToken ? "customerStrongToken" : "consumerToken",
            language,
            body,
            params
        )
    }

    async updateBankAccount(
        customer_id: string,
        bank_account_id: string,
        body: BankAccountBody,
        params?: CustomerParams,
        language?: Language
    ) {
        return await this.api.patch<BankAccountResponse>(
            `/customer/${customer_id}/payment/bank_account/${bank_account_id}`,
            this.state.customerStrongToken ? "customerStrongToken" : "consumerToken",
            language,
            body,
            params
        )
    }

    async removeBankAccount(
        customer_id: string,
        bank_account_id: string,
        params?: CustomerParams,
        language?: Language
    ) {
        return await this.api.delete(
            `/customer/${customer_id}/payment/bank_account/${bank_account_id}`,
            this.state.customerStrongToken ? "customerStrongToken" : "consumerToken",
            language,
            params
        )
    }

    async requestPaymentToken(
        customer_id: string,
        body: PaymentTokenBody,
        params?: CustomerParams,
        language?: Language
    ) {
        return await this.api.post<PaymentTokenResponse>(
            `/customer/${customer_id}/payment/request-token`,
            "customerWeakToken",
            language,
            body,
            params
        )
    }

    async confirmPaymentToken(
        customer_id: string,
        body: ConfirmPaymentTokenBody,
        params?: CustomerParams,
        language?: Language
    ) {
        return await this.api.post<ConfirmPaymentTokenResponse>(
            `/customer/${customer_id}/payment/confirm-token`,
            "customerWeakToken",
            language,
            body,
            params
        )
    }

    async createCustomerTracking(body: CustomerTrackingBody, language?: Language) {
        return await this.api.post("/customer/tracking", "consumerToken", language, body)
    }

    async getCustomerAggregation(body: CustomerAggregationBody) {
        return await this.api.post("/customer/aggregation", "consumerToken", undefined, body)
    }

    async getCustomerSegmentation(body: CustomerSegmentationBody) {
        return await this.api.post("/customer/segmentation", "consumerToken", undefined, body)
    }

    async getCustomerAttributes(body: CustomerAttributesBody) {
        return await this.api.post<CustomerAttributesResponse>("/customer/attributes", "consumerToken", undefined, body)
    }

    async getCustomerExpression(customer_id: string, expression_id: string) {
        return await this.api.get<CustomerExpressionResponse>(
            `/customer/${customer_id}/expression/${expression_id}`,
            "customerWeakToken"
        )
    }

    // #endregion Customer
    // #region Subscription

    async getSubscriptions(customer_id: string, language?: Language) {
        return await this.api.get<SubscriptionResponse[]>(
            `/customer/${customer_id}/subscriptions`,
            "customerWeakToken",
            language
        )
    }

    async getSubscription(customer_id: string, subscription_id: string, language?: Language) {
        return await this.api.get<SubscriptionResponse>(
            `/customer/${customer_id}/subscription/${subscription_id}`,
            "customerWeakToken",
            language
        )
    }

    async updateSubscription(
        customer_id: string,
        subscription_id: string,
        body: SubscriptionBody,
        language?: Language
    ) {
        return await this.api.patch<SubscriptionResponse>(
            `/customer/${customer_id}/subscription/${subscription_id}`,
            "customerWeakToken",
            language,
            body
        )
    }

    async getSubscriptionTokenLink(token: string, language?: Language) {
        return await this.api.get<SubscriptionTokenLinkResponse>(
            `/subscription/token_link/${token}`,
            "consumerToken",
            language
        )
    }

    async updateSubscriptionTokenLink(token: string, body: SubscriptionTokenLinkBody, language?: Language) {
        return await this.api.patch<SubscriptionTokenLinkResponse>(
            `/subscription/token_link/${token}`,
            "consumerToken",
            language,
            body
        )
    }

    async getSubscriptionShippingCarriers(customer_id: string, address_id: string, language?: Language) {
        return await this.api.get<SubscriptionShippingCarriersResponse>(
            `/customer/${customer_id}/address/${address_id}/shippings`,
            "customerWeakToken",
            language
        )
    }

    async getSubscriptionPaymentMethods(subscription_id: string, language?: Language) {
        return await this.api.get<SubscriptionPaymentMethodsResponse>(
            `/subscription/${subscription_id}/payments`,
            "customerWeakToken",
            language
        )
    }

    // #endregion Subscription
    // #region Order

    async getOrders(customer_id: string, params?: OrderParams, language?: Language) {
        return await this.api.get<OrdersResponse>(
            `/customer/${customer_id}/orders`,
            "customerWeakToken",
            language,
            params
        )
    }

    async getOrder(customer_id: string, order_id: string, language?: Language) {
        return await this.api.get<OrderResponse>(
            `/customer/${customer_id}/order/${order_id}`,
            "customerWeakToken",
            language
        )
    }

    async updateOrder(customer_id: string, order_id: string, body: OrderBody, language?: Language) {
        return await this.api.patch<OrderResponse>(
            `/customer/${customer_id}/order/${order_id}`,
            "customerWeakToken",
            language,
            body
        )
    }

    // #endregion Order
    // #region Cart & Product

    async getCart(cart_id: string, params: CartParams, language?: Language) {
        try {
            return await this.api.get<CartResponse>(`/cart/${cart_id}`, "consumerToken", language, params)
        } catch (err) {
            const error = err as APIError
            if (error.status === 404 || error.status === 403) {
                this.state.cart_id = undefined
                this.state.cart_access_token = undefined
            }
            throw error
        }
    }

    async createCart(body: CartBody, language?: Language) {
        const cart = await this.api.post<CartResponse>("/cart", "consumerToken", language, body)
        this.state.cart_id = cart.id
        this.state.cart_access_token = cart.meta.access_token
        return cart
    }

    async updateCart(cart_id: string, body: CartBody, params: CartParams, language?: Language) {
        return await this.api.patch<CartResponse>(`/cart/${cart_id}`, "consumerToken", language, body, params)
    }

    async createCartLine(cart_id: string, body: CartItemBody, params: CartParams, language?: Language) {
        return await this.api.post<CartResponse>(`/cart/${cart_id}/line`, "consumerToken", language, body, params)
    }

    async updateCartLine(
        cart_id: string,
        cart_line_id: string,
        body: CartItemBody,
        params: CartParams,
        language?: Language
    ) {
        return await this.api.patch<CartResponse>(
            `/cart/${cart_id}/line/${cart_line_id}`,
            "consumerToken",
            language,
            body,
            params
        )
    }

    async deleteCartLine(cart_id: string, cart_line_id: string, params: CartParams, language?: Language) {
        return await this.api.delete(`/cart/${cart_id}/line/${cart_line_id}`, "consumerToken", language, params)
    }

    async updateCartVoucher(cart_id: string, body: CartVoucherBody, params: CartParams, language?: Language) {
        return await this.api.patch<CartResponse>(`/cart/${cart_id}/redeem`, "consumerToken", language, body, params)
    }

    async getCartShippingCarriers(cart_id: string, params: CartParams, language?: Language) {
        return await this.api.get<CartShippingResponse>(`/cart/${cart_id}/shippings`, "consumerToken", language, params)
    }

    async getCartFinanceProviders(cart_id: string, params: CartParams, language?: Language) {
        return await this.api.get<CartPaymentResponse>(`/cart/${cart_id}/payments`, "consumerToken", language, params)
    }

    async postCartConfirm(cart_id: string, body: CartConfirmBody, params: CartParams, language?: Language) {
        return await this.api.post<CartConfirmResponse>(
            `/cart/${cart_id}/confirm`,
            "consumerToken",
            language,
            body,
            params
        )
    }

    clearCart() {
        this.state.historic_cart_id = this.state.cart_id
        this.state.cart_id = undefined
        this.state.historic_cart_access_token = this.state.cart_access_token
        this.state.cart_access_token = undefined
        return
    }

    async getProducts(params?: ProductsParams, language?: Language) {
        return await this.api.get<ProductsResponse>("/products", "consumerToken", language, params)
    }

    // #endregion Cart & Product
    // #region Miscellaneous

    async postContact(body: ContactBody, language?: Language) {
        return await this.api.post("/contact", "consumerToken", language, body)
    }

    async getContactCategories(language?: Language) {
        return await this.api.get<ContactCategoryResponse>("/contact/category", "consumerToken", language)
    }

    // #endregion Miscellaneous
    // #region Partner Promotional

    async getCommissionPromotionalGroups(params?: PromotionalGroupParams, language?: Language) {
        return await this.api.get<PromotionalGroupResponse>("/promotional/group", "customerWeakToken", language, params)
    }

    async getCommissionPromotionalCouponsAggregate(
        aggregation_function: string,
        field: string,
        params?: PromotionalParams,
        language?: Language
    ) {
        return await this.api.get<PromotionalResponse>(
            `/promotional/coupon/aggregate/${aggregation_function}/${field}`,
            "customerWeakToken",
            language,
            params
        )
    }

    // #endregion Partner Promotional
    // #region Partner Commission

    async getCommissionRules(params?: CommissionRulesParams, language?: Language) {
        return await this.api.get<CommissionRulesResponse>("/commission/rules", "customerWeakToken", language, params)
    }

    async getCommissionRule(id: string, language?: Language) {
        return await this.api.get<CommissionRuleResponse>(`/commission/rule/${id}`, "customerWeakToken", language)
    }

    async getCommissionBookings(params?: CommissionBookingsParams, language?: Language) {
        return await this.api.get<CommissionBookingsResponse>(
            "/commission/bookings",
            "customerWeakToken",
            language,
            params
        )
    }

    async getCommissionBookingAggregate(
        aggregation_function: string,
        field: string,
        params?: CommissionBookingsAggregateParams,
        language?: Language
    ) {
        return await this.api.get<CommissionBookingsAggregateResponse>(
            `/commission/booking/aggregate/${aggregation_function}/${field}`,
            "customerWeakToken",
            language,
            params
        )
    }

    async getCommissionStatements(params?: CommissionStatementsParams, language?: Language) {
        return await this.api.get<CommissionStatementsResponse>(
            "/commission/statements",
            "customerWeakToken",
            language,
            params
        )
    }

    async getCommissionStatement(id: string, language?: Language) {
        return await this.api.get<CommissionStatementResponse>(
            `/commission/statement/${id}`,
            "customerWeakToken",
            language
        )
    }

    async getCommissionStatementPdf(id: string, language?: Language) {
        return await this.api.get<string>(`/commission/statement/${id}/download`, "customerWeakToken", language)
    }

    // #endregion Partner Commission
    // #region Partner Research

    async getCommissionResearches(params?: ResearchParams, language?: Language) {
        return await this.api.get<ResearchesResponse>("/commission/researches", "customerWeakToken", language, params)
    }

    async createCommissionResearch(body: ResearchBody, language?: Language) {
        return await this.api.post<ResearchResponse>("/commission/research", "customerWeakToken", language, body)
    }

    // #endregion Partner Research
    // #region Partner Event

    async getActivityEvents(params?: EventsParams, language?: Language) {
        return await this.api.get<EventsResponse>("/activity/events", "customerWeakToken", language, params)
    }

    async getActivityEvent(id: number, language?: Language) {
        return await this.api.get<EventResponse>(`/activity/event/${id}`, "customerWeakToken", language)
    }

    async getActivityCategories(params?: EventCategoryParams, language?: Language) {
        return await this.api.get<EventCategoryResponse>("/activity/categories", "customerWeakToken", language, params)
    }

    async getActivityAttendances(params?: EventAttendancesParams, language?: Language) {
        return await this.api.get<EventAttendancesResponse>(
            "/activity/attendances",
            "customerWeakToken",
            language,
            params
        )
    }

    async createActivityAttendance(event_id: number, body: EventCreateAttendancesBody, language?: Language) {
        return await this.api.post<EventCreateAttendancesResponse>(
            `/activity/event/${event_id}/attendance`,
            "customerWeakToken",
            language,
            body
        )
    }

    async deleteActivityAttendance(id: string, language?: Language) {
        return await this.api.delete(`/activity/attendances/${id}`, "customerWeakToken", language)
    }

    // #endregion Partner Event
}
